import React, { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import ChatEngine from './ChatEngine';

/**
 * Quick Chat Button - Opens chat directly with a specific user
 * Use this on profile pages, bid listings, etc.
 */
const QuickChatButton = ({
  targetUserId,
  targetUserName = 'User',
  initialMessage = '',
  variant = 'primary', // 'primary', 'secondary', 'icon'
  size = 'md', // 'sm', 'md', 'lg'
  className = ''
}) => {
  const { user } = useAuth();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatRoomId, setChatRoomId] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleStartChat = async () => {
    if (!user?.userId || !targetUserId) return;

    try {
      setLoading(true);
      const response = await api.post(
        `/chat/rooms/direct?userId=${user.userId}`,
        { targetUserId, initialMessage }
      );
      setChatRoomId(response.data.id);
      setIsChatOpen(true);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg'
  };

  const variantClasses = {
    primary: 'bg-collaborb-primary text-white hover:bg-collaborb-primary-dark',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300',
    icon: 'p-2 bg-gray-100 hover:bg-gray-200 rounded-full'
  };

  return (
    <>
      <button
        onClick={handleStartChat}
        disabled={loading || !user?.userId || user?.userId === targetUserId}
        className={`inline-flex items-center gap-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variantClasses[variant]} ${variant !== 'icon' ? sizeClasses[size] : ''} ${className}`}
        title={`Message ${targetUserName}`}
      >
        {loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current"></div>
        ) : (
          <MessageSquare className={`${size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'}`} />
        )}
        {variant !== 'icon' && <span>Message</span>}
      </button>

      <ChatEngine
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        initialChatRoomId={chatRoomId}
      />
    </>
  );
};

export default QuickChatButton;

